import { createDataDogLogger } from '../hooks/useDataDogLogger';
import { removeQuotesFromText } from './textHelpers';

/**
 * An error that carries copy intended for the user. Only trusted producers
 * set `userMessage`: the HTTP transports (4xx backend `detail`) and the auth
 * service wrapper (translated Cognito errors).
 */
export interface UserFacingError {
  userMessage: string;
}

export const hasUserFacingMessage = (
  error: unknown,
): error is UserFacingError => {
  if (error === null || typeof error !== 'object') {
    return false;
  }

  const candidate = (error as { userMessage?: unknown }).userMessage;

  return typeof candidate === 'string' && candidate.trim().length > 0;
};

/**
 * Sanitizer for CWE-209 (information exposure through an error message).
 *
 * Returns text that is safe to render: the error's `userMessage` when a
 * trusted producer attached one, otherwise `fallback`. The raw `message`
 * (network failures, JSON parse errors, 5xx bodies, SDK internals,
 * programming errors) is never returned; it is reported to Datadog RUM
 * for diagnostics.
 *
 * `fallback` is passed through untouched, so it can be an i18n key when the
 * sink translates (e.g. `useAppMessages`) or already-translated text when
 * the sink renders verbatim (e.g. `ErrorCard`).
 */
export const getUserFacingErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (hasUserFacingMessage(error)) {
    return removeQuotesFromText(error.userMessage) || fallback;
  }

  createDataDogLogger({ source: 'getUserFacingErrorMessage' }).error(
    error instanceof Error ? error : new Error(String(error)),
    { hiddenFromUser: true },
  );

  return fallback;
};
